import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { Button, TextView } from '@atoms';
import { BillingAccountSelector } from '@organisms';
import { DateRangePicker, Range } from 'react-date-range';
import Popover from '@mui/material/Popover';
import EventIcon from '@mui/icons-material/EventOutlined';
import FileDownloadIcon from '@mui/icons-material/FileDownloadOutlined';
import { DateTime } from 'luxon';
import { materialTheme } from '@particles';
import ExportReportsDialog from './ExportReportsDialog';
interface HeaderProps {
  onSelectRange?: (start: DateTime, end: DateTime) => void;
  onSelectedAccount: (billingAccount?: BillingAccount) => void;
}

const Header: React.FC<HeaderProps> = (props: HeaderProps) => {
  const { onSelectRange, onSelectedAccount } = props;
  const [selectedAccount, setSelectedAccount] = useState<BillingAccount | null>(null);
  const [exportOpen, setExportOpen] = useState<boolean>(false);

  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  const [range, setRange] = useState<Range[]>([
    {
      startDate: DateTime.now().minus({ days: 6 }).toJSDate(),
      endDate: DateTime.now().toJSDate(),
      key: 'selection'
    }
  ]);

  useEffect(() => {
    const now = DateTime.now();
    const start = range[0]?.startDate ? DateTime.fromJSDate(range[0].startDate) : now;
    const end = range[0]?.endDate ? DateTime.fromJSDate(range[0].endDate) : now;

    // Both boundaries must cover whole days. `end` was already pushed to
    // 23:59:59; `start` was not, and the default range seeds startDate from
    // `DateTime.now().minus({ days: 6 })`, which carries the current clock
    // time. Srts rows are stamped at midnight (one row per aggregated day),
    // so a start of e.g. 14:32 fell *after* the first day's row and silently
    // dropped a whole day from every consumer of this range — Total Consumed
    // SRT, AI, WABA, Twilio and Negotiator alike. Only the default range was
    // affected; picking dates in the picker returns midnight already, which
    // is why the numbers looked self-consistent.
    const newStart = start.startOf('day');
    const newEnd = end.set({ hour: 23, minute: 59, second: 59 });
    onSelectRange && onSelectRange(newStart, newEnd);
  }, [range]);

  const handleAccountChange = (account: BillingAccount | null) => {
    // null selection = "All Accounts" (upstream expects undefined).
    setSelectedAccount(account);
    onSelectedAccount(account ?? undefined);
  };

  return (
    <>
      <Box sx={{ flexDirection: 'row', display: 'flex', alignItems: 'center' }}>
        <Box sx={{ flex: 1 }} />
        <TextView $fontWeight={400} $fontSize={14} mr={1}>
          Filters
        </TextView>
        <Box sx={{ mr: 1 }}>
          <BillingAccountSelector
            title="All Accounts"
            status="both"
            caller="analytics-header"
            width={260}
            value={selectedAccount}
            onSelected={handleAccountChange}
          />
        </Box>
        <Button
          aria-describedby={id}
          sx={{ paddingLeft: 5, color: materialTheme.palette.text.primary, mr: 2 }}
          startIcon={<EventIcon />}
          variant={'outlined'}
          onClick={handleClick}>
          <TextView $fontWeight={400}>
            {DateTime.fromJSDate(range[0].startDate ? range[0]?.startDate : new Date()).toFormat(
              'LLLL, dd yyyy'
            )}
            {` - `}
            {DateTime.fromJSDate(range[0].endDate ? range[0]?.endDate : new Date()).toFormat(
              'LLLL, dd yyyy'
            )}
          </TextView>
        </Button>
        <Button
          label="Export Reports"
          startIcon={<FileDownloadIcon />}
          onClick={() => setExportOpen(true)}
          size="small"
        />
        <Popover
          id={id}
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left'
          }}>
          <DateRangePicker
            onChange={(item) => setRange([item.selection])}
            moveRangeOnFirstSelection={false}
            months={2}
            ranges={range}
            direction="horizontal"
          />
        </Popover>
      </Box>
      <ExportReportsDialog
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        startDate={range[0]?.startDate}
        endDate={range[0]?.endDate}
        billingAccountId={selectedAccount?.id}
        locationId={selectedAccount?.locationId}
        billingAccountName={selectedAccount?.companyName}
      />
    </>
  );
};
export default Header;
